import React from 'react';
import { createSelector } from 'reselect';
import FormControl from '@material-ui/core/FormControl';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';
import LinearProgress from '@material-ui/core/LinearProgress';
import { connect } from 'react-redux';
import lodash from 'lodash';
import { IRootState } from '../../../../../@types/redux';
import { IGeoServerBlock } from '../../../../../types/model/masterData/geoserver/block';
import MasterDataGeoServerFunctions from '../../../../../store/masterData/geoserver/functions';
import { LngLatBoundsLike } from 'react-map-gl';
import GeoHelper from '../../../../../services/helper/geoHelper';

interface IGeoServerSubDivisionAutocompleteProps {
    id ?: string;
    value ?: string | null;

    onChange : (value ?: string, bounds ?: LngLatBoundsLike | null, event ?: React.ChangeEvent<unknown>) => void;

    blocks : Array<IGeoServerBlock>;

    required ?: boolean;
    autoFocus ?: boolean;

    fullWidth ?: boolean;

    isLoading : boolean;
    disabled ?: boolean;
    name ?: string;

    label ?: string;

    divisionGuid ?: string | null;
}

interface IGeoServerSubDivisionAutocompleteState {
}

class GeoServerSubDivisionAutocompleteComponent extends React.Component<IGeoServerSubDivisionAutocompleteProps, IGeoServerSubDivisionAutocompleteState> {
    constructor(props : IGeoServerSubDivisionAutocompleteProps) {
        super(props);

        this.state = {
        };
    }

    public readonly componentDidMount = () => {
        this.loadData();
    };

    public readonly loadData = async () => {
        MasterDataGeoServerFunctions.getBlockList();
    };

    private readonly getData = (state : IGeoServerSubDivisionAutocompleteState, props : IGeoServerSubDivisionAutocompleteProps) => props.blocks;
    private readonly getValue = (state : IGeoServerSubDivisionAutocompleteState, props : IGeoServerSubDivisionAutocompleteProps) => props.value;
    private readonly getRequired = (state : IGeoServerSubDivisionAutocompleteState, props : IGeoServerSubDivisionAutocompleteProps) => props.required;
    private readonly getDivisionGuid = (state : IGeoServerSubDivisionAutocompleteState, props : IGeoServerSubDivisionAutocompleteProps) => props.divisionGuid;
    
    private readonly getBlockAutocomplete = createSelector([
        this.getData,
        this.getRequired,
        this.getDivisionGuid,
    ], (
        blocks,
        required,
        divisionGuid,
    ) => {
        const blocksDrop = lodash.chain(blocks)
            .filter(x => !divisionGuid || x.divisionGuid === divisionGuid)
            .uniqBy(n => n.subDivisionGuid)
            .map(n => ({
                label: n.subDivisionName,
                value: n.subDivisionGuid,
            }))
            .sortBy(n => n.label)
            .value();

        if (!required) {
            blocksDrop.unshift({
                label: 'ALL',
                value: '',
            });
        }

        return blocksDrop;
    });

    private readonly getSelectedValue = createSelector([
        this.getValue,
        this.getRequired,
        this.getBlockAutocomplete,
    ], (
        value,
        required,
        blocks,
    ) => {
        if (value && typeof(value) === 'string') {
            const block = blocks
                .find(x => x.value === value);

            return block ?? null;
        }

        if (!value && !required) {
            return {
                label: 'ALL',
                value: '',
            };
        }

        return null;
    });



    private readonly onChange = (event : React.ChangeEvent<unknown>, value : {
        label : string;
        value : string;
    } | null) => {
        const bounds = !value?.value ? null : GeoHelper.getPolygonBounds(
            this.props.blocks
                .filter(x => !this.props.divisionGuid || x.divisionGuid === this.props.divisionGuid)
                .filter(x => x.subDivisionGuid === value.value)
        );

        this.props.onChange(value?.value, bounds, event);
    };

    public readonly render = () => {
        const {
            required,
            fullWidth,
            isLoading,
            disabled,
            label,
        } = this.props;

        const blocks = this.getBlockAutocomplete(this.state, this.props);

        const value = this.getSelectedValue(this.state, this.props);
        return (
            <React.Fragment>
                <FormControl fullWidth={fullWidth} error={required && !value} required={required}>
                    <Autocomplete
                        disabled={isLoading || disabled}
                        id='block_select'
                        options={blocks}
                        value={value}
                        getOptionSelected={(option, val) => option.value === val.value}
                        getOptionLabel={option => option.label}
                        onChange={this.onChange}
                        disableClearable={required}
                        openOnFocus
                        renderInput={params => (
                            <TextField
                                error={required && !value}
                                required={required}
                                {...params}
                                fullWidth={fullWidth}
                                label={label ?? 'Sub Division'}
                            />)}
                    />
                    {
                        required && !value &&
                            <FormHelperText error>Required</FormHelperText>
                    }
                </FormControl>
                <div className='wfill' style={{
                    minHeight: 8,
                }}>
                    {
                        isLoading &&
                        <LinearProgress />
                    }
                </div>
            </React.Fragment>
        );
    };
}

const mapStateToProps = (state : IRootState) => {
    return {
        blocks: state.masterData.geoserver.blocks,
        isLoading: state.masterData.geoserver.isLoadingBlocks,
    };
};

const GeoServerSubDivisionAutocomplete = connect(
    mapStateToProps,
)(GeoServerSubDivisionAutocompleteComponent);

export default GeoServerSubDivisionAutocomplete;
